import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useAppSelector } from "src/redux/hooks";
import { cgmManager } from "src/services/cgm/cgmManager";

export type CgmSyncStatus = "idle" | "syncing" | "synced" | "failed";

interface CgmSyncButtonProps {
  className?: string;
  "aria-label"?: string;
}

const RESET_TEXT_MS = 2500;

/**
 * Sync button with rotation animation and status feedback.
 * Reads cgmGetLatest + cgmHardSync from Redux for loading/error.
 */
const CgmSyncButton: React.FC<CgmSyncButtonProps> = ({
  className = "",
  "aria-label": ariaLabel = "Resync CGM data",
}) => {
  const { cgmHardSync, cgmGetLatest, cgmMissingSync } = useAppSelector(
    (state) => state.cgm
  );
  const isLoading =
    cgmGetLatest.isLoading || cgmHardSync.isLoading || cgmMissingSync.isLoading;
  const error = cgmGetLatest.error ?? cgmHardSync.error ?? cgmMissingSync.error;
  const [statusText, setStatusText] = useState<string | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLoadingRef = useRef(isLoading);

  const status: CgmSyncStatus = isLoading
    ? "syncing"
    : statusText === "Synced"
    ? "synced"
    : statusText === "Sync failed"
    ? "failed"
    : "idle";

  // On transition from loading to done: show synced/failed, then reset
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading) {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      setStatusText(error ? "Sync failed" : "Synced");
      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null;
        setStatusText(null);
      }, RESET_TEXT_MS);
    }
  }, [isLoading, error]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  const onSync = useCallback(() => {
    cgmManager.syncMissingPoints();
    // cgmManager.getLatestReading();
  }, []);

  const handleClick = useCallback(() => {
    if (!isLoading) onSync();
  }, [onSync, isLoading]);

  const textColorClass =
    status === "syncing"
      ? "text-gray-500"
      : status === "synced"
      ? "text-green-600"
      : status === "failed"
      ? "text-red-600"
      : "text-gray-500";

  return (
    <div className={className}>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`p-1.5 rounded-full flex items-center gap-1 transition-colors ${
          isLoading
            ? "bg-gray-100 cursor-wait"
            : "bg-gray-100 active:bg-gray-200 hover:bg-gray-150"
        }`}
        aria-label={ariaLabel}
      >
        <RefreshCw
          className={`w-4 h-4 flex-shrink-0 ${
            isLoading ? "animate-spin" : ""
          } ${
            status === "synced"
              ? "text-green-600"
              : status === "failed"
              ? "text-red-600"
              : "text-gray-600"
          }`}
        />
        {(isLoading || statusText) && (
          <span className={`text-xs font-medium ${textColorClass}`}>
            {isLoading ? "Syncing" : statusText}
          </span>
        )}
      </button>
    </div>
  );
};

export default CgmSyncButton;
